import { type DocumentVariableMetadata, documentVariableGroup } from './document-variable'

/**
 * CLIENTE PRINCIPAL (titular) de la negociación, como escalares.
 *
 * A diferencia de la colección `clientes` —que queda fuera del paquete—, el titular SIEMPRE
 * existe: es el dueño por defecto. Cómo se resuelve según el tipo de cliente (individual → el
 * propio cliente; jurídico → el representante legal por defecto, más los campos del nombramiento)
 * es cosa del `resolve` del host; acá vive lo que se OFRECE.
 */

const PERSONAL_NOTE =
  'Jurídico: corresponde al representante legal por defecto (la empresa no lo tiene).'
const APPOINTMENT_NOTE =
  'Solo aplica a cliente jurídico (dato del nombramiento del representante legal).'

export const TITULAR_VARIABLE_METADATA: DocumentVariableMetadata[] = documentVariableGroup({
  prefix: 'titular',
  category: 'owner',
  groupLabel: 'Cliente principal',
  fields: {
    // ── Tipo de cliente (para comparar y ramificar el documento) ──
    tipo_cliente: {
      label: 'Cliente principal · Tipo de cliente',
      type: 'enum',
      description:
        'Individual o Jurídico. Úsalo en condicionales para mostrar texto distinto según el tipo.',
      optionsFromHost: true,
      example: 'individual'
    },
    // ── Persona (individual: el cliente; jurídico: el representante legal) ──
    nombre: {
      label: 'Cliente principal · Nombre',
      description: 'Individual: nombre del cliente. Jurídico: nombre del representante legal.',
      example: 'MARÍA MARGARITA ALONSO ASCENCIO'
    },
    dpi_numeros: {
      label: 'Cliente principal · DPI (números)',
      description: PERSONAL_NOTE,
      example: '1943 64194 0101'
    },
    dpi_letras: {
      label: 'Cliente principal · DPI (en letras)',
      description: PERSONAL_NOTE
    },
    edad: {
      label: 'Cliente principal · Edad (en letras)',
      description: PERSONAL_NOTE,
      example: 'cuarenta y dos'
    },
    genero: {
      label: 'Cliente principal · Género',
      type: 'enum',
      description: PERSONAL_NOTE,
      optionsFromHost: true,
      example: 'F'
    },
    estado_civil: {
      label: 'Cliente principal · Estado civil',
      description: PERSONAL_NOTE,
      example: 'soltera'
    },
    nacionalidad: {
      label: 'Cliente principal · Nacionalidad',
      description: PERSONAL_NOTE,
      example: 'guatemalteca'
    },
    profesion: {
      label: 'Cliente principal · Profesión',
      description: PERSONAL_NOTE,
      example: 'Abogada y Notaria'
    },
    pais: {
      label: 'Cliente principal · País',
      type: 'enum',
      description: PERSONAL_NOTE,
      optionsFromHost: true,
      example: 'Guatemala'
    },
    departamento: {
      label: 'Cliente principal · Departamento',
      type: 'enum',
      description: PERSONAL_NOTE,
      optionsFromHost: true,
      example: 'Sacatepéquez'
    },
    fecha_nacimiento: {
      label: 'Cliente principal · Fecha de nacimiento',
      type: 'date',
      description: PERSONAL_NOTE,
      example: '1984-03-12'
    },
    // ── Contacto ──
    nit: {
      label: 'Cliente principal · NIT',
      description: 'Individual: NIT del cliente. Jurídico: NIT de la empresa.',
      example: '1234567-8'
    },
    email: {
      label: 'Cliente principal · Email',
      description: 'Individual: del cliente. Jurídico: de la empresa.',
      example: 'maria.alonso@email.com'
    },
    telefono: {
      label: 'Cliente principal · Teléfono',
      description: 'Individual: del cliente. Jurídico: de la empresa.',
      example: '5555-5555'
    },
    residencia_a_su_nombre: {
      label: 'Cliente principal · Recibo a su nombre',
      type: 'enum',
      description:
        'Si la vivienda donde reside está a su nombre. Sólo cliente INDIVIDUAL — el ' +
        'representante legal de una empresa no la declara.',
      // `value` = el TEXTO QUE IMPRIME el resolver, no un booleano ni una clave. Misma regla que
      // `pais` y `departamento`: un condicional del editor comparando contra `true` no matchearía
      // nunca, porque lo que el orquestador deja en `ctx.titular` es la palabra.
      options: [
        { value: 'sí', label: 'Sí' },
        { value: 'no', label: 'No' }
      ],
      example: 'sí'
    },
    residencia_justificacion: {
      label: 'Cliente principal · Recibo — justificación',
      description: 'Por qué la vivienda no está a su nombre. Vacío cuando sí lo está.',
      example: 'La vivienda es de su cónyuge'
    },
    // ── Cómo comparece ──
    figura: {
      label: 'Cliente principal · Figura (cómo comparece)',
      type: 'enum',
      description: 'propia (individual) o juridica (representante de una empresa).',
      options: [
        { value: 'propia', label: 'En nombre propio' },
        { value: 'juridica', label: 'Persona jurídica (rep. legal)' },
        { value: 'gestor', label: 'Gestor de negocios' }
      ],
      example: 'propia'
    },
    // ── Entidad + nombramiento del representante (solo cliente jurídico) ──
    entidad: {
      label: 'Cliente principal · Entidad (razón social)',
      description: APPOINTMENT_NOTE,
      example: 'Inmobiliaria del Valle, S.A.'
    },
    cargo: {
      label: 'Cliente principal · Cargo del nombramiento',
      description: APPOINTMENT_NOTE,
      example: 'Administrador Único'
    },
    nombramiento_fecha: {
      label: 'Cliente principal · Nombramiento — fecha inicial',
      type: 'date',
      description: APPOINTMENT_NOTE
    },
    nombramiento_fecha_final: {
      label: 'Cliente principal · Nombramiento — fecha final',
      type: 'date',
      description: `${APPOINTMENT_NOTE} Vacío si el plazo es indefinido.`
    },
    tipo_plazo: {
      label: 'Cliente principal · Tipo de plazo del nombramiento',
      type: 'enum',
      description: APPOINTMENT_NOTE,
      optionsFromHost: true,
      example: 'I'
    },
    registro_nombre: {
      label: 'Cliente principal · Registro (nombre)',
      description: APPOINTMENT_NOTE,
      example: 'Registro Mercantil'
    },
    registro_numero: {
      label: 'Cliente principal · Registro — número',
      description: APPOINTMENT_NOTE
    },
    registro_folio: {
      label: 'Cliente principal · Registro — folio',
      description: APPOINTMENT_NOTE
    },
    registro_libro: {
      label: 'Cliente principal · Registro — libro',
      description: APPOINTMENT_NOTE
    },
    notario_nombre: {
      label: 'Cliente principal · Notario (nombre)',
      description: APPOINTMENT_NOTE
    },
    notario_tratamiento: {
      label: 'Cliente principal · Notario (el/la)',
      description: APPOINTMENT_NOTE,
      example: 'el notario'
    }
  }
})
